import {gql} from '@apollo/client';
import {partition} from '@benchling/js-utils';

import {DomainGraphInterface} from 'app-core/framework/CoreObjectConstants';
import {isCoreObjectWithInterface} from 'app-core/framework/CoreObjectIntrospectionUtils';
import * as CoreObjectArchiveActions from 'app-core/platform/archivable/CoreObjectArchiveActions';
import {
  ArchiveRecord,
  BulkArchiveItemsResponse,
  BulkUnarchiveItemsResponse,
  OTHER_PURPOSE,
} from 'archive/ArchiveConstants';
import * as ArchivePurposesUtil from 'archive/ArchivePurposesUtil';
import {ArchiveOptionsForModal} from 'archive/components/modals/ArchiveModal';
import {assert} from 'Asserts';
import {DataModificationsHeader} from 'audit/DataModifications';
import {callAsyncWithDataModifications, callWithDataModifications} from 'audit/DataModificationsModal';
import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin, ANALYTICS_MODULE} from 'constants/AnalyticsConstants';
import {EventConstants} from 'constants/EventConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as FileBrowserDAO from 'file-browser/FileBrowserDAO';
import {BulkArchiveFilesResponse, BulkUnarchiveFilesResponse} from 'file-browser/FileBrowserDAO';
import FolderDAOCache from 'file-browser/FolderDAOCache';
import SourceDAO from 'file-browser/SourceDAO';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {writeFragmentsToCache} from 'graphql-client/ApolloWrappers';
import {getDefaultTypenameResolver} from 'graphql-client/declarative-translations/TypenameResolver';
import FragmentWrapper from 'graphql-client/FragmentWrapper';
import * as ConnectionDAO from 'instruments/connections/ConnectionDAO';
import AppDispatcher from 'lib/Dispatcher';
import navigateToURL from 'lib/navigateToURL';
import {optimisticallyUpdate} from 'lib/OptimisticallyUpdate';
import TemplateCollectionDAO from 'notebook/entry-templates/collections/TemplateCollectionDAO';
import notifications from 'notifications';
import * as PipelineDAO from 'pipelines/PipelineDAO';
import * as ProcessManagementDAO from 'processes/dao/ProcessManagementDAO';
import {ProcessManagementItemType} from 'processes/ProcessConstants';
import {getStore, Thunk} from 'redux-stores/Store';
import RegistryActions from 'registry/RegistryActions';
import * as RegistryDAO from 'registry/RegistryDAO';
import SchemaFieldSelectorDAO from 'registry/schema-field-selector/SchemaFieldSelectorDAO';
import * as RequestSchemaReduxActions from 'requests/schemas/RequestSchemaReduxActions';
import * as RequestTaskSchemaDAO from 'requests/schemas/RequestTaskSchemaDAO';
import * as RequestTaskSchemaReduxActions from 'requests/schemas/RequestTaskSchemaReduxActions';
import * as AssaySchemaDAO from 'results/assay-schemas/AssaySchemaDAO';
import {writeResultArchiveRecordsToCache} from 'results/assays/AssayRecordActions';
import * as AssayRecordDAO from 'results/assays/AssayRecordDAO';
import {
  getInventoryRegistryChangeSummary,
  InventoryRegistryDataModAction,
} from 'samples/getInventoryRegistryChangeSummary';
import * as StorableDAO from 'samples/StorableDAO';
import * as StorableSchemaDAO from 'samples/StorableSchemaDAO';
import * as TagSchemaDAO from 'tag-schema/TagSchemaDAO';
import {BulkArchiveTagSchemasResponse} from 'tag-schema/TagSchemaDAO';
import {debouncedGlobalChipRefresh} from 'ui-library/chips/useSmartChipQuery';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import APIUtil from 'util/APIUtil';
import {withLoadingScreen} from 'util/Promises';

import {ArchiveRecordGraphQL} from '../../graphql-fragments/app-core/platform/archivable/__generated/ArchiveRecordGraphQL';
import {HasArchiveRecordGraphQL} from '../../graphql-fragments/archive/__generated/HasArchiveRecordGraphQL';

const BLANK_ARCHIVE_RECORD_GQL: ArchiveRecordGraphQL = {
  __typename: 'ArchiveRecordNode',
  purpose: OTHER_PURPOSE,
};

function archiveSuccess(
  response: BulkArchiveItemsResponse,
  actionType:
    | typeof EventConstants.BULK_ARCHIVE_ASSAY_SCHEMAS_COMPLETED
    | typeof EventConstants.BULK_ARCHIVE_STORABLES_COMPLETED
    | typeof EventConstants.BULK_ARCHIVE_PROCESS_MANAGEMENT
    | typeof EventConstants.BULK_ARCHIVE_CONNECTIONS
    | typeof EventConstants.BULK_ARCHIVE_TEMPLATE_COLLECTIONS_COMPLETED
    | typeof EventConstants.BULK_ARCHIVE_ANALYSES_COMPLETED
    | typeof EventConstants.BULK_ARCHIVE_FILES_COMPLETED
    | typeof EventConstants.BULK_ARCHIVE_REGISTRIES_COMPLETED
    | typeof EventConstants.BULK_ARCHIVE_STORABLE_SCHEMAS_COMPLETED
    | typeof EventConstants.BULK_ARCHIVE_SCHEMA_FIELD_SELECTORS_COMPLETED
    | typeof EventConstants.BULK_ARCHIVE_TAG_SCHEMAS_COMPLETED
    | typeof EventConstants.BULK_ARCHIVE_SOURCES_COMPLETED,
  extra: {} = {}
): void {
  notifications.success('Items successfully archived.');
  AppDispatcher.dispatch({
    actionType,
    itemIds: response.archivedItemIds,
    ...extra,
  });
}

function unarchiveSuccess(
  response: BulkUnarchiveItemsResponse,
  actionType:
    | typeof EventConstants.BULK_UNARCHIVE_ASSAY_SCHEMAS_COMPLETED
    | typeof EventConstants.BULK_UNARCHIVE_STORABLES_COMPLETED
    | typeof EventConstants.BULK_UNARCHIVE_PROCESS_MANAGEMENT
    | typeof EventConstants.BULK_UNARCHIVE_CONNECTIONS
    | typeof EventConstants.BULK_UNARCHIVE_TEMPLATE_COLLECTIONS_COMPLETED
    | typeof EventConstants.BULK_UNARCHIVE_ANALYSES_COMPLETED
    | typeof EventConstants.BULK_UNARCHIVE_FILES_COMPLETED
    | typeof EventConstants.BULK_UNARCHIVE_STORABLE_SCHEMAS_COMPLETED
    | typeof EventConstants.BULK_UNARCHIVE_SCHEMA_FIELD_SELECTORS_COMPLETED
    | typeof EventConstants.BULK_UNARCHIVE_TAG_SCHEMAS_COMPLETED
    | typeof EventConstants.BULK_UNARCHIVE_SOURCES_COMPLETED,
  extra: {} = {}
): void {
  notifications.success('Items successfully unarchived.');
  AppDispatcher.dispatch({
    actionType,
    itemIds: response.unarchivedItemIds,
    ...extra,
  });
}

// Overrides the purpose to other if archive purposes are not supported for the items.
export function overridePurpose(purpose: string, ids: Array<string>): string {
  if (!ArchivePurposesUtil.areArchivePurposesVisible(ids)) {
    return OTHER_PURPOSE;
  }
  return purpose;
}

function _invalidateFolders(itemIds: Array<string>): void {
  const archivedFolderIds = itemIds.filter((apiId) => APIUtil.isFolder(apiId));
  archivedFolderIds.forEach((id) => {
    FolderDAOCache.invalidateFolder(id);
    // eslint-disable-next-line no-restricted-syntax
    for (const key in FolderDefaultsKey) {
      if (FolderDefaults.get(FolderDefaultsKey[key as keyof typeof FolderDefaultsKey]) === id) {
        FolderDefaults.unset(FolderDefaultsKey[key as keyof typeof FolderDefaultsKey]);
        break;
      }
    }
  });
}

export async function bulkArchiveAssaySchemas(ids: Array<string>, purpose: string): Promise<void> {
  const response = await withLoadingScreen(
    AssaySchemaDAO.bulkArchive(ids, overridePurpose(purpose, ids)),
    'Archiving...'
  );
  const {archiveRecordsByItemId} = response;
  archiveSuccess(response, EventConstants.BULK_ARCHIVE_ASSAY_SCHEMAS_COMPLETED, {archiveRecordsByItemId});
  getStore().dispatch(
    RequestSchemaReduxActions.requestSchemasArchived(response.archivedItemIds, archiveRecordsByItemId)
  );
}

export async function bulkUnarchiveAssaySchemas(ids: Array<string>): Promise<void> {
  const response = await AssaySchemaDAO.bulkUnarchive(ids);
  unarchiveSuccess(response, EventConstants.BULK_UNARCHIVE_ASSAY_SCHEMAS_COMPLETED);
  getStore().dispatch(RequestSchemaReduxActions.requestSchemasUnarchived(response.unarchivedItemIds));
}

export function bulkArchiveRequestTaskSchemas(ids: Array<string>, purpose: string): Thunk<Promise<void>> {
  return async (dispatch) => {
    const response = await withLoadingScreen(
      RequestTaskSchemaDAO.bulkArchive(ids, overridePurpose(purpose, ids)),
      'Archiving...'
    );
    dispatch(
      RequestTaskSchemaReduxActions.requestTaskSchemasArchived(
        response.archivedItemIds,
        response.archiveRecordsByItemId
      )
    );
  };
}

export function bulkUnarchiveRequestTaskSchemas(ids: Array<string>): Thunk<Promise<void>> {
  return async (dispatch) => {
    const response = await RequestTaskSchemaDAO.bulkUnarchive(ids);
    dispatch(RequestTaskSchemaReduxActions.requestTaskSchemasUnarchived(response.unarchivedItemIds));
  };
}

export async function bulkArchiveStorablesWithDataMods(
  ids: Array<string>,
  purpose: string,
  {shouldRemoveBarcodes, shouldArchiveContainedSamples}: ArchiveOptionsForModal,
  onSuccess?: () => void
): Promise<void> {
  const areParentStorablesPresent = ids.some((id) =>
    [ModelTypeConstants.BOX, ModelTypeConstants.LOCATION, ModelTypeConstants.PLATE].includes(
      APIUtil.apiIdToType(id)
    )
  );

  await callAsyncWithDataModifications({
    changeSummary: getInventoryRegistryChangeSummary({
      apiIds: ids,
      action: InventoryRegistryDataModAction.ARCHIVE,
      modifyContainedSamples: shouldArchiveContainedSamples ?? false,
      modifyContainedStorables: areParentStorablesPresent,
    }),
    onPerformAction: async (dataModsHeader?: DataModificationsHeader) => {
      await bulkArchiveStorables(
        ids,
        purpose,
        {
          shouldRemoveBarcodes,
          shouldArchiveContainedSamples: shouldArchiveContainedSamples ?? false,
        },
        onSuccess,
        dataModsHeader
      );
    },
    // TODO(PLAN-1063): Do we need to handle child storables having different projects?
    apiIds: ids,
    analyticsParams: {
      module: 'SAMPLES',
      actionOrigin: ActionOrigin.INVENTORY_REGISTRY_SEARCH_UI,
      modifiedAction: InventoryRegistryDataModAction.ARCHIVE,
      numModifiedTotalItems: ids.length,
      entryId: null,
    },
    useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_INVENTORY,
  });
}

const StorableArchiveRecordFragment: FragmentWrapper<HasArchiveRecordGraphQL> = {
  fragment: gql`
    fragment StorableArchiveRecordFragment on StorableInterface {
      __typename
      id
      archiveRecord {
        purpose
      }
    }
  `,
  fragmentName: 'StorableArchiveRecordFragment',
};

const BioentityArchiveRecordFragment: FragmentWrapper<HasArchiveRecordGraphQL> = {
  fragment: gql`
    fragment BioentityArchiveRecordFragment on RegistryItemInterface {
      __typename
      id
      archiveRecord {
        purpose
      }
    }
  `,
  fragmentName: 'BioentityArchiveRecordFragment',
};

function refreshChipsArchiveStatus(ids: Array<string>, purpose: string | null): void {
  const storableIds = [];
  const bioentityIds = [];
  const otherIds = [];
  for (const id of ids) {
    if (APIUtil.isStorable(id)) {
      storableIds.push(id);
    } else if (APIUtil.isBiologicalEntity(id)) {
      bioentityIds.push(id);
    } else {
      otherIds.push(id);
    }
  }

  const typenameResolver = getDefaultTypenameResolver();

  if (storableIds.length > 0) {
    writeFragmentsToCache(
      StorableArchiveRecordFragment,
      storableIds.map(
        (id) =>
          ({
            __typename: typenameResolver.getTypenameFromID(id),
            id,
            archiveRecord: purpose ? {...BLANK_ARCHIVE_RECORD_GQL, purpose} : null,
          }) as HasArchiveRecordGraphQL
      )
    );
  }

  if (bioentityIds.length > 0) {
    const archiveRecord: ArchiveRecordGraphQL | null = purpose
      ? {__typename: 'ArchiveRecordNode', purpose}
      : null;
    writeFragmentsToCache(
      BioentityArchiveRecordFragment,
      bioentityIds.map(
        (id) =>
          ({
            __typename: typenameResolver.getTypenameFromID(id),
            id,
            archiveRecord,
          }) as HasArchiveRecordGraphQL
      )
    );
  }

  if (bioentityIds.length > 0 || storableIds.length > 0) {
    debouncedGlobalChipRefresh();
  }
}

export async function bulkArchiveStorables(
  ids: Array<string>,
  purpose: string,
  {
    shouldRemoveBarcodes,
    shouldArchiveContainedSamples,
  }: {shouldRemoveBarcodes: boolean; shouldArchiveContainedSamples: boolean},
  onSuccess?: () => void,
  dataModsHeader?: DataModificationsHeader
): Promise<void> {
  LoadingScreen.show('Archiving...');
  await optimisticallyUpdate<
    {
      ids: Array<string>;
      purpose: string | null;
    },
    BulkArchiveItemsResponse | undefined
  >({
    persistData: async () =>
      await StorableDAO.bulkArchive(
        ids,
        overridePurpose(purpose, ids),
        {
          shouldRemoveBarcodes,
          shouldArchiveContainedSamples,
        },
        dataModsHeader
      ).done((response: BulkArchiveItemsResponse) => {
        archiveSuccess(response, EventConstants.BULK_ARCHIVE_STORABLES_COMPLETED);
        onSuccess?.();
        // Response may include additional ids in case of `shouldArchiveContainedSamples`
        const additionalIds = response.archivedItemIds.filter((id) => !ids.includes(id));
        refreshChipsArchiveStatus(additionalIds, purpose);
      }),
    optimisticData: {ids, purpose},
    revertData: {ids, purpose: null},
    writeDataToCache: ({purpose}: {ids: Array<string>; purpose: string | null}): void => {
      refreshChipsArchiveStatus(ids, purpose);
    },
  })
    .catch(notifications.notifyError)
    .finally(() => {
      LoadingScreen.hide();
    });
}

export async function bulkArchiveProcessManagement(
  itemType: ProcessManagementItemType,
  ids: Array<string>,
  purpose: string
): Promise<ProcessManagementDAO.BulkArchiveResponse | void> {
  try {
    const response = await withLoadingScreen(
      ProcessManagementDAO.bulkArchive(itemType, ids, overridePurpose(purpose, ids)),
      'Archiving...'
    );
    const archiveRecordsByItemId = Object.fromEntries(ids.map((id) => [id, {purpose}]));
    archiveSuccess(response, EventConstants.BULK_ARCHIVE_PROCESS_MANAGEMENT, {archiveRecordsByItemId});
    return response;
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (e) {
    notifications.notifyError(e);
  }
}

export async function bulkUnarchiveProcessManagement(
  itemType: ProcessManagementItemType,
  ids: Array<string>
): Promise<ProcessManagementDAO.BulkUnarchiveResponse | void> {
  try {
    const response = await withLoadingScreen(
      ProcessManagementDAO.bulkUnarchive(itemType, ids),
      'Unarchiving...'
    );
    unarchiveSuccess(response, EventConstants.BULK_UNARCHIVE_PROCESS_MANAGEMENT);
    return response;
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (e) {
    notifications.notifyError(e);
  }
}

export async function bulkArchiveConnections(ids: Array<string>, purpose: string): Promise<void> {
  try {
    const response = await withLoadingScreen(
      ConnectionDAO.bulkArchive(ids, overridePurpose(purpose, ids)),
      'Archiving...'
    );
    archiveSuccess(response, EventConstants.BULK_ARCHIVE_CONNECTIONS);
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (e) {
    notifications.notifyError(e);
  }
}

export async function bulkUnarchiveConnections(ids: Array<string>): Promise<void> {
  try {
    const response = await withLoadingScreen(ConnectionDAO.bulkUnarchive(ids), 'Unarchiving...');
    unarchiveSuccess(response, EventConstants.BULK_UNARCHIVE_CONNECTIONS);
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (e) {
    notifications.notifyError(e);
  }
}

export async function bulkArchiveTemplateCollections(
  ids: Array<string>,
  purpose: string,
  bulkArchiveFileOptions: BulkArchiveFilesOptions,
  postArchiveRefetch?: () => void
): Promise<void> {
  try {
    const response = await withLoadingScreen(
      TemplateCollectionDAO.bulkArchive(ids, overridePurpose(purpose, ids)),
      'Archiving...'
    );
    archiveSuccess(response, EventConstants.BULK_ARCHIVE_TEMPLATE_COLLECTIONS_COMPLETED);
    if (postArchiveRefetch != null) {
      postArchiveRefetch();
    }
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (e) {
    notifications.notifyError(e);
  }
}

export async function bulkUnarchiveTemplateCollections(
  ids: Array<string>,
  unarchiveOptions?: {hasRegisteredItems: boolean},
  postUnarchiveRefetch?: () => void
): Promise<void> {
  try {
    const response = await withLoadingScreen(TemplateCollectionDAO.bulkUnarchive(ids), 'Unarchiving...');
    unarchiveSuccess(response, EventConstants.BULK_UNARCHIVE_TEMPLATE_COLLECTIONS_COMPLETED);
    if (postUnarchiveRefetch) {
      postUnarchiveRefetch();
    }
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (e) {
    notifications.notifyError(e);
  }
}

export async function bulkArchiveAnalyses(
  ids: Array<string>,
  purpose: string,
  {resultPurpose}: Pick<BulkArchiveFilesOptions, 'resultPurpose'>
): Promise<void> {
  try {
    const response = await withLoadingScreen(
      PipelineDAO.bulkArchivePipelines(ids, purpose, resultPurpose ?? null),
      'Archiving...'
    );
    archiveSuccess(response, EventConstants.BULK_ARCHIVE_ANALYSES_COMPLETED);
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (e) {
    notifications.notifyError(e);
  }
}

export async function bulkUnarchiveAnalyses(ids: Array<string>): Promise<void> {
  try {
    const response = await withLoadingScreen(PipelineDAO.bulkUnarchivePipelines(ids), 'Unarchiving...');
    unarchiveSuccess(response, EventConstants.BULK_UNARCHIVE_ANALYSES_COMPLETED);
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (e) {
    notifications.notifyError(e);
  }
}

function getBulkArchiveFilesTrackingModule(hasStorableItems: boolean): keyof typeof ANALYTICS_MODULE {
  if (hasStorableItems) {
    return 'SAMPLES';
  }
  return 'REGISTRY';
}
export interface BulkArchiveFilesOptions {
  shouldRemoveShortcuts: boolean;
  shouldUnlinkParts: boolean | null;
  shouldUnregisterBioentities: boolean;
  unregisteredBioentitiesFolderId: string | null;
  parentFolderIds: Array<string>;
  shouldRemoveBarcodes: boolean;
  hasRegisteredItems?: boolean;
  shouldArchiveContainedSamples: boolean;
  resultPurpose?: string | null;
}

export async function bulkArchiveFilesWithDataMods(
  ids: Array<string>,
  purpose: string,
  bulkArchiveFilesOptions: BulkArchiveFilesOptions,
  postArchiveRefetch?: () => void
): Promise<void> {
  const entityIds = ids.filter((id) => APIUtil.isBiologicalEntity(id));
  const storableIds = ids.filter((id) => APIUtil.isStorable(id));
  const folderIds = ids.filter((id) => APIUtil.isFolder(id));
  const areParentStorablesPresent = storableIds.some((id) =>
    [ModelTypeConstants.BOX, ModelTypeConstants.LOCATION, ModelTypeConstants.PLATE].includes(
      APIUtil.apiIdToType(id)
    )
  );
  const isArchivingFolders = folderIds.length > 0;
  const hasRegisteredItemsOrStorables = bulkArchiveFilesOptions.hasRegisteredItems || storableIds.length > 0;
  const useProjectLevelDataModsLogic =
    BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_INVENTORY ||
    BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_REGISTRY;
  const isDataModsNeeded = isArchivingFolders || hasRegisteredItemsOrStorables;

  const apiIds = [...entityIds, ...storableIds, ...folderIds];

  await callWithDataModifications({
    changeSummary: isDataModsNeeded
      ? getInventoryRegistryChangeSummary({
          apiIds,
          action: InventoryRegistryDataModAction.ARCHIVE,
          modifyContainedStorables: areParentStorablesPresent,
          modifyContainedSamples: bulkArchiveFilesOptions.shouldArchiveContainedSamples,
        })
      : '',
    onPerformAction: (dataModsHeader?: DataModificationsHeader) => {
      bulkArchiveFiles(ids, purpose, bulkArchiveFilesOptions, postArchiveRefetch, dataModsHeader);
    },
    apiIds,
    analyticsParams: {
      module: getBulkArchiveFilesTrackingModule(storableIds.length > 0),
      actionOrigin: ActionOrigin.INVENTORY_REGISTRY_SEARCH_UI,
      modifiedAction: InventoryRegistryDataModAction.ARCHIVE,
      numModifiedTotalItems: apiIds.length,
      entryId: null,
    },
    useProjectLevelDataModsLogic: useProjectLevelDataModsLogic && isDataModsNeeded,
  });
}

export function bulkArchiveFiles(
  ids: Array<string>,
  purpose: string,
  bulkArchiveFilesOptions: BulkArchiveFilesOptions,
  postArchiveRefetch?: () => void,
  dataModsHeader?: DataModificationsHeader
): void {
  const [coreObjectArchiveIds, legacyArchiveIds] = partition(ids, (id) =>
    isCoreObjectWithInterface({id}, DomainGraphInterface.CORE_OBJECT_ARCHIVABLE)
  );
  legacyBulkArchiveFiles(
    legacyArchiveIds,
    purpose,
    bulkArchiveFilesOptions,
    postArchiveRefetch,
    dataModsHeader
  );
  CoreObjectArchiveActions.coreObjectArchiveAction(
    coreObjectArchiveIds,
    purpose,
    bulkArchiveFilesOptions,
    undefined,
    dataModsHeader
  );
}

// Handles archiving for files that do not implement core object archiving
function legacyBulkArchiveFiles(
  ids: Array<string>,
  purpose: string,
  {
    shouldRemoveShortcuts,
    shouldUnlinkParts,
    shouldUnregisterBioentities,
    unregisteredBioentitiesFolderId,
    parentFolderIds,
    shouldRemoveBarcodes,
    shouldArchiveContainedSamples,
    resultPurpose,
  }: BulkArchiveFilesOptions,
  postArchiveRefetch?: () => void,
  dataModsHeader?: DataModificationsHeader
): void {
  if (ids.length === 0) {
    return;
  }
  LoadingScreen.show('Archiving...');
  FileBrowserDAO.bulkArchive(
    ids,
    overridePurpose(purpose, ids),
    {
      shouldRemoveShortcuts,
      shouldUnlinkParts,
      shouldUnregisterBioentities,
      unregisteredBioentitiesFolderId,
      shouldRemoveBarcodes,
      shouldArchiveContainedSamples,
      resultPurpose,
    },
    dataModsHeader
  )
    .done((response: BulkArchiveFilesResponse) => {
      archiveSuccess(response, EventConstants.BULK_ARCHIVE_FILES_COMPLETED, {
        permissionsByItemId: response.permissionsByItemId,
        archiveRecordsByItemId: response.archiveRecordsByItemId,
      });
      if (postArchiveRefetch != null) {
        postArchiveRefetch();
      }
      _invalidateFolders(response.archivedItemIds);
      if (shouldUnregisterBioentities && unregisteredBioentitiesFolderId != null) {
        RegistryActions.dispatchForUnregister(
          Object.values(response.unregisterDataByItemId),
          unregisteredBioentitiesFolderId,
          parentFolderIds,
          response.permissionsByItemId
        );
      }
      // This will update the GraphQL fragments only for assay results with the new archive record data
      // Other fragments remain untouched. This is enforced by using the Result fragment specifically when
      // reading from the cache and only updating if the read value is not null.
      writeResultArchiveRecordsToCache(response.archiveRecordsByItemId);
      refreshChipsArchiveStatus(
        response.archivedItemIds.filter((id) => APIUtil.isBiologicalEntity(id)),
        purpose
      );
    })
    .fail(notifications.notifyError)
    .always(() => {
      LoadingScreen.hide();
    });
}

export async function bulkArchiveSourcesWithDataMods(
  ids: Array<string>,
  purpose: string,
  {
    shouldRemoveShortcuts,
    shouldUnregisterBioentities,
    unregisteredBioentitiesFolderId,
    parentFolderIds,
  }: {
    shouldRemoveShortcuts: boolean;
    shouldUnregisterBioentities: boolean;
    unregisteredBioentitiesFolderId: string | null;
    parentFolderIds: Array<string>;
  }
): Promise<void> {
  await callWithDataModifications({
    changeSummary: getInventoryRegistryChangeSummary({
      apiIds: ids,
      action: InventoryRegistryDataModAction.ARCHIVE,
      modifyContainedSamples: true,
      modifyContainedStorables: true,
    }),
    onPerformAction: (dataModsHeader?: DataModificationsHeader) => {
      bulkArchiveSources(
        ids,
        purpose,
        {
          shouldRemoveShortcuts,
          shouldUnregisterBioentities,
          unregisteredBioentitiesFolderId,
          parentFolderIds,
        },
        dataModsHeader
      );
    },
    apiIds: ids,
    analyticsParams: {
      module: 'NAVIGATION',
      actionOrigin: ActionOrigin.INVENTORY_REGISTRY_SEARCH_UI,
      modifiedAction: InventoryRegistryDataModAction.ARCHIVE,
      numModifiedTotalItems: ids.length,
      entryId: null,
    },
    useProjectLevelDataModsLogic:
      BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_INVENTORY ||
      BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_REGISTRY,
  });
}

export function bulkArchiveSources(
  ids: Array<string>,
  purpose: string,
  {
    shouldRemoveShortcuts,
    shouldUnregisterBioentities,
    unregisteredBioentitiesFolderId,
    parentFolderIds,
  }: {
    shouldRemoveShortcuts: boolean;
    shouldUnregisterBioentities: boolean;
    unregisteredBioentitiesFolderId: string | null;
    parentFolderIds: Array<string>;
  },
  dataModsHeader?: DataModificationsHeader
): void {
  LoadingScreen.show('Archiving...');
  SourceDAO.bulkArchive(
    ids,
    overridePurpose(purpose, ids),
    {
      shouldUnregisterBioentities,
      unregisteredBioentitiesFolderId,
      shouldRemoveShortcuts,
    },
    dataModsHeader
  )
    .done((response: BulkArchiveFilesResponse) => {
      _invalidateFolders(response.archivedItemIds);
      archiveSuccess(response, EventConstants.BULK_ARCHIVE_SOURCES_COMPLETED, {
        permissionsByItemId: response.permissionsByItemId,
        archiveRecordsByItemId: response.archiveRecordsByItemId,
      });
      if (shouldUnregisterBioentities && unregisteredBioentitiesFolderId != null) {
        RegistryActions.dispatchForUnregister(
          Object.values(response.unregisterDataByItemId),
          unregisteredBioentitiesFolderId,
          parentFolderIds,
          response.permissionsByItemId
        );
      }
    })
    .fail(notifications.notifyError)
    .always(() => {
      LoadingScreen.hide();
    });
}

export function bulkArchiveRegistries(ids: Array<string>, purpose: string): void {
  assert(ids.length === 1, 'Currently only archiving a single registry is supported');
  LoadingScreen.show('Archiving...');
  RegistryDAO.bulkArchive(ids, overridePurpose(purpose, ids))
    .done((response: BulkArchiveItemsResponse) => {
      archiveSuccess(response, EventConstants.BULK_ARCHIVE_REGISTRIES_COMPLETED, {
        registryId: ids[0],
      });
      navigateToURL('/editor');
    })
    .fail(notifications.notifyError)
    .always(() => {
      LoadingScreen.hide();
    });
}

export function bulkArchiveTagSchemas(ids: Array<string>, purpose: string): Promise<void> {
  LoadingScreen.show('Archiving...');
  return TagSchemaDAO.bulkArchive(ids, overridePurpose(purpose, ids))
    .then((response: BulkArchiveTagSchemasResponse) => {
      notifications.success('Items successfully archived.');
    })
    .catch(notifications.notifyError)
    .finally(() => {
      LoadingScreen.hide();
    });
}

export function bulkArchiveStorableSchemas(ids: Array<string>, purpose: string): void {
  LoadingScreen.show('Archiving...');
  StorableSchemaDAO.bulkArchive(ids, overridePurpose(purpose, ids))
    .done((response: StorableSchemaDAO.BulkArchiveResponse) => {
      archiveSuccess(response, EventConstants.BULK_ARCHIVE_STORABLE_SCHEMAS_COMPLETED, {
        archiveRecordsByItemId: response.archiveRecordsByItemId,
      });
    })
    .fail(notifications.notifyError)
    .always(() => {
      LoadingScreen.hide();
    });
}

export function bulkArchiveSchemaFieldSelectors(ids: Array<string>, purpose: string): void {
  LoadingScreen.show('Archiving...');
  SchemaFieldSelectorDAO.bulkArchive(ids, overridePurpose(purpose, ids))
    .done(
      (response: BulkArchiveItemsResponse & {archiveRecordsByItemId: {[itemId: string]: ArchiveRecord}}) => {
        archiveSuccess(response, EventConstants.BULK_ARCHIVE_SCHEMA_FIELD_SELECTORS_COMPLETED, {
          archiveRecordsByItemId: response.archiveRecordsByItemId,
        });
      }
    )
    .fail(notifications.notifyError)
    .always(() => {
      LoadingScreen.hide();
    });
}

export async function bulkUnarchiveStorablesWithDataMods(
  ids: Array<string>,
  onSuccess?: () => void
): Promise<void> {
  const areParentStorablesPresent = ids.some((id) =>
    [ModelTypeConstants.BOX, ModelTypeConstants.LOCATION, ModelTypeConstants.PLATE].includes(
      APIUtil.apiIdToType(id)
    )
  );
  await callAsyncWithDataModifications({
    changeSummary: getInventoryRegistryChangeSummary({
      apiIds: ids,
      action: InventoryRegistryDataModAction.UNARCHIVE,
      modifyContainedStorables: areParentStorablesPresent,
    }),
    onPerformAction: async (dataModsHeader?: DataModificationsHeader) => {
      await bulkUnarchiveStorables(ids, onSuccess, dataModsHeader);
    },
    // TODO(PLAN-1063): Do we need to handle child storables having different projects?
    apiIds: ids,
    analyticsParams: {
      module: 'SAMPLES',
      actionOrigin: ActionOrigin.INVENTORY_REGISTRY_SEARCH_UI,
      modifiedAction: InventoryRegistryDataModAction.UNARCHIVE,
      numModifiedTotalItems: ids.length,
      entryId: null,
    },
    useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_INVENTORY,
  });
}

export async function bulkUnarchiveStorables(
  ids: Array<string>,
  onSuccess?: () => void,
  dataModsHeader?: DataModificationsHeader
): Promise<void> {
  await StorableDAO.bulkUnarchive(ids, dataModsHeader)
    .done((response: BulkUnarchiveItemsResponse) => {
      unarchiveSuccess(response, EventConstants.BULK_UNARCHIVE_STORABLES_COMPLETED);
      onSuccess?.();
    })
    .fail(notifications.notifyError);
}

export async function bulkUnarchiveFilesWithDataMods(
  ids: Array<string>,
  {hasRegisteredItems}: {hasRegisteredItems?: boolean} = {},
  postUnarchiveRefetch?: () => void
): Promise<void> {
  const entityIds = ids.filter((id) => APIUtil.isBiologicalEntity(id));
  const storableIds = ids.filter((id) => APIUtil.isStorable(id));
  const folderIds = ids.filter((id) => APIUtil.isFolder(id));
  const areParentStorablesPresent = storableIds.some((id) =>
    [ModelTypeConstants.BOX, ModelTypeConstants.LOCATION, ModelTypeConstants.PLATE].includes(
      APIUtil.apiIdToType(id)
    )
  );
  const isUnarchivingFolders = folderIds.length > 0;
  const hasRegisteredItemsOrStorables = hasRegisteredItems || storableIds.length > 0;
  const apiIds = [...entityIds, ...storableIds, ...folderIds];

  const useProjectLevelDataModsLogic =
    BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_INVENTORY ||
    BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_REGISTRY;
  const isDataModsNeeded = isUnarchivingFolders || hasRegisteredItemsOrStorables;

  await callWithDataModifications({
    changeSummary: isDataModsNeeded
      ? getInventoryRegistryChangeSummary({
          apiIds,
          action: InventoryRegistryDataModAction.UNARCHIVE,
          modifyContainedStorables: areParentStorablesPresent,
        })
      : '',
    onPerformAction: (dataModsHeader?: DataModificationsHeader) => {
      bulkUnarchiveFiles(ids, postUnarchiveRefetch, dataModsHeader);
    },
    apiIds,
    analyticsParams: {
      module: getBulkArchiveFilesTrackingModule(storableIds.length > 0),
      actionOrigin: ActionOrigin.INVENTORY_REGISTRY_SEARCH_UI,
      modifiedAction: InventoryRegistryDataModAction.UNARCHIVE,
      numModifiedTotalItems: apiIds.length,
      entryId: null,
    },
    useProjectLevelDataModsLogic: useProjectLevelDataModsLogic && isDataModsNeeded,
  });
}

export function bulkUnarchiveFiles(
  ids: Array<string>,
  postUnarchiveRefetch?: () => void,
  dataModsHeader?: DataModificationsHeader
): void {
  const [coreObjectArchiveIds, legacyArchiveIds] = partition(ids, (id) =>
    isCoreObjectWithInterface({id}, DomainGraphInterface.CORE_OBJECT_ARCHIVABLE)
  );
  legacyBulkUnarchiveFiles(legacyArchiveIds, postUnarchiveRefetch, dataModsHeader);
  CoreObjectArchiveActions.coreObjectUnarchiveAction(coreObjectArchiveIds);
}

// Handles unarchiving for files that do not implement core object archiving
function legacyBulkUnarchiveFiles(
  ids: Array<string>,
  postUnarchiveRefetch?: () => void,
  dataModsHeader?: DataModificationsHeader
): void {
  if (ids.length === 0) {
    return;
  }
  LoadingScreen.show('Unarchiving...');
  FileBrowserDAO.bulkUnarchive(ids, dataModsHeader)
    .done((response: BulkUnarchiveFilesResponse) => {
      _invalidateFolders(response.unarchivedItemIds);
      if (postUnarchiveRefetch) {
        postUnarchiveRefetch();
      }
      refreshChipsArchiveStatus(
        response.unarchivedItemIds.filter((id) => APIUtil.isBiologicalEntity(id)),
        null
      );
      unarchiveSuccess(response, EventConstants.BULK_UNARCHIVE_FILES_COMPLETED, {
        permissionsByItemId: response.permissionsByItemId,
      });
    })
    .fail(notifications.notifyError)
    .always(() => {
      LoadingScreen.hide();
    });
}

export async function bulkUnarchiveSourcesWithDataMods(ids: Array<string>): Promise<void> {
  await callWithDataModifications({
    changeSummary: getInventoryRegistryChangeSummary({
      apiIds: ids,
      action: InventoryRegistryDataModAction.UNARCHIVE,
      modifyContainedSamples: true,
      modifyContainedStorables: true,
    }),
    onPerformAction: (dataModsHeader?: DataModificationsHeader) => {
      bulkUnarchiveSources(ids, dataModsHeader);
    },
    apiIds: ids,
    analyticsParams: {
      module: 'NAVIGATION',
      actionOrigin: ActionOrigin.INVENTORY_REGISTRY_SEARCH_UI,
      modifiedAction: InventoryRegistryDataModAction.UNARCHIVE,
      numModifiedTotalItems: ids.length,
      entryId: null,
    },
    useProjectLevelDataModsLogic:
      BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_INVENTORY ||
      BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_REGISTRY,
  });
}

export function bulkUnarchiveSources(ids: Array<string>, dataModsHeader?: DataModificationsHeader): void {
  SourceDAO.bulkUnarchive(ids, dataModsHeader)
    .done((response: BulkUnarchiveFilesResponse) => {
      _invalidateFolders(response.unarchivedItemIds);
      unarchiveSuccess(response, EventConstants.BULK_UNARCHIVE_SOURCES_COMPLETED, {
        permissionsByItemId: response.permissionsByItemId,
      });
    })
    .fail(notifications.notifyError);
}

export function bulkUnarchiveTagSchemas(ids: Array<string>): Promise<void> {
  return TagSchemaDAO.bulkUnarchive(ids)
    .then((response: BulkUnarchiveItemsResponse) => {
      notifications.success('Items successfully unarchived.');
    })
    .catch(notifications.notifyError);
}

export function bulkUnarchiveStorableSchemas(ids: Array<string>): void {
  StorableSchemaDAO.bulkUnarchive(ids)
    .done((response: BulkUnarchiveItemsResponse) => {
      unarchiveSuccess(response, EventConstants.BULK_UNARCHIVE_STORABLE_SCHEMAS_COMPLETED);
    })
    .fail(notifications.notifyError);
}

export function bulkUnarchiveSchemaFieldSelectors(ids: Array<string>): void {
  SchemaFieldSelectorDAO.bulkUnarchive(ids)
    .done((response: BulkUnarchiveItemsResponse) => {
      unarchiveSuccess(response, EventConstants.BULK_UNARCHIVE_SCHEMA_FIELD_SELECTORS_COMPLETED);
    })
    .fail(notifications.notifyError);
}

export enum RunArchiveActionType {
  ARCHIVE_RUNS_COMPLETED = 'ARCHIVE_RUNS_COMPLETED',
  UNARCHIVE_RUNS_COMPLETED = 'UNARCHIVE_RUNS_COMPLETED',
}

interface ArchiveRunsCompletedAction {
  type: RunArchiveActionType.ARCHIVE_RUNS_COMPLETED;
  ids: Array<string>;
  purpose: string;
}

// Runs use separate actions from the rest because it's in redux instead of flux
export const ARCHIVE_RUNS_COMPLETED = 'ARCHIVE_RUNS_COMPLETED';
export function assayRunsArchiveSuccess(ids: Array<string>, purpose: string): ArchiveRunsCompletedAction {
  return {
    type: RunArchiveActionType.ARCHIVE_RUNS_COMPLETED,
    ids,
    purpose,
  };
}

interface UnarchiveRunsCompletedAction {
  type: RunArchiveActionType.UNARCHIVE_RUNS_COMPLETED;
  ids: Array<string>;
}

export const UNARCHIVE_RUNS_COMPLETED = 'UNARCHIVE_RUNS_COMPLETED';
export function assayRunsUnarchiveSuccess(ids: Array<string>): UnarchiveRunsCompletedAction {
  return {
    type: RunArchiveActionType.UNARCHIVE_RUNS_COMPLETED,
    ids,
  };
}

export type RunArchiveAction = ArchiveRunsCompletedAction | UnarchiveRunsCompletedAction;

export function bulkArchiveAssayRuns(ids: Array<string>, purpose: string): Thunk<void> {
  return async (dispatch) => {
    const {archivedItemIds} = await AssayRecordDAO.archiveAssayRuns(ids, purpose);
    dispatch(assayRunsArchiveSuccess(archivedItemIds, purpose));
  };
}

export function bulkUnarchiveAssayRuns(ids: Array<string>): Thunk<void> {
  return async (dispatch) => {
    const {unarchivedItemIds} = await AssayRecordDAO.unarchiveAssayRuns(ids);
    dispatch(assayRunsUnarchiveSuccess(unarchivedItemIds));
  };
}
